import { useState, useEffect } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { MoneyInput } from '../components/MoneyInput';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { FileCheck, FileText, Plus, Eye, X, Pencil, Trash2, Mail, MessageCircle, Ban, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { downloadPdf } from '../utils/downloadPdf';
import { Attachments } from '../components/Attachments';
import { fmtDate } from '../lib/datetime';
import { useArticleUnits } from '../hooks/useArticleUnits';
import { SearchableSelect } from '../components/SearchableSelect';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper para verificar permisos
const userCan = (user, module, action) => {
  if (!user) return false;
  if (user.role === 'admin') return true;
  if (user.permissions) {
    return user.permissions[module]?.[action] === true;
  }
  return false;
};

function PurchaseOrders() {
  const { user } = useOutletContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const { unitOf } = useArticleUnits();
  
  // Permisos del usuario
  const canCreate = userCan(user, 'ordenes_compra', 'crear');
  const canEdit = userCan(user, 'ordenes_compra', 'editar');
  const canApprove = userCan(user, 'ordenes_compra', 'aprobar');
  const canReject = userCan(user, 'ordenes_compra', 'rechazar');
  
  // Filtro especial desde Dashboard
  const statusFromUrl = searchParams.get('status') || '';
  
  const [orders, setOrders] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [editForm, setEditForm] = useState({ supplier_id: '', observations: '', budget: '' });
  const [selectedOrder, setSelectedOrder] = useState(null);
  
  const [formData, setFormData] = useState({
    request_id: '',
    supplier: '',
    supplier_id: '',
    observations: '',
    budget: ''
  });
  
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [activeSuppliers, setActiveSuppliers] = useState([]);
  const supplierOptions = activeSuppliers.map(s => ({ value: s.supplier_id, label: s.business_name, hint: s.tax_id ? `CUIT ${s.tax_id}` : '' }));

  // v3.38.0 — Aviso de circuito cruzado: proveedor mayoritariamente "Otras Compras"
  const [supplierUsage, setSupplierUsage] = useState(null); // {receptions, other_purchases, is_mostly_other,...}
  useEffect(() => {
    const sid = formData.supplier_id;
    if (!sid) { setSupplierUsage(null); return; }
    (async () => {
      try {
        const r = await axios.get(`${BACKEND_URL}/api/suppliers/${sid}/imputation-suggestion`, { withCredentials: true });
        setSupplierUsage(r.data?.usage_profile || null);
      } catch { setSupplierUsage(null); }
    })();
  }, [formData.supplier_id]);
  
  // Filtros de búsqueda - inicializar con valor de URL si existe
  const [filters, setFilters] = useState({
    order_number: '',
    request_number: '',
    supplier: '',
    status: statusFromUrl
  });
  
  // Función para limpiar filtro especial
  const clearStatusFilter = () => {
    setFilters(prev => ({ ...prev, status: '' }));
    setSearchParams({});
  };
  
  useEffect(() => {
    fetchData();
  }, []);
  
  const fetchData = async () => {
    try {
      // Cargar OC (obligatorio)
      const ordersRes = await axios.get(`${BACKEND_URL}/api/purchase-orders`, { withCredentials: true });
      setOrders(ordersRes.data);
      
      // Cargar NP pendientes solo si tiene permiso de crear OC
      if (canCreate) {
        try {
          const requestsRes = await axios.get(`${BACKEND_URL}/api/purchase-requests`, { withCredentials: true });
          const approvedRequests = requestsRes.data.filter(r => r.status === 'approved');
          const ocRequestIds = ordersRes.data.map(o => o.request_id);
          const pending = approvedRequests.filter(r => !ocRequestIds.includes(r.request_id));
          setPendingRequests(pending);
        } catch (err) {
          setPendingRequests([]);
        }
        // Cargar proveedores activos para el dropdown
        try {
          const supRes = await axios.get(`${BACKEND_URL}/api/suppliers/active`, { withCredentials: true });
          setActiveSuppliers(supRes.data || []);
        } catch (err) {
          setActiveSuppliers([]);
        }
      }
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };
  
  const handleRequestSelect = (requestId) => {
    const request = pendingRequests.find(r => r.request_id === requestId);
    setSelectedRequest(request);
    setFormData({ ...formData, request_id: requestId });
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.supplier_id) {
      toast.error('Selecciona un proveedor del catálogo');
      return;
    }
    
    try {
      await axios.post(`${BACKEND_URL}/api/purchase-orders`, formData, { withCredentials: true });
      toast.success('Orden de Compra generada');
      setCreateDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al generar OC');
    }
  };
  
  const resetForm = () => {
    setFormData({ request_id: '', supplier: '', supplier_id: '', observations: '', budget: '' });
    setSelectedRequest(null);
  };
  
  const handleDownloadPDF = (orderId, orderNumber) => {
    downloadPdf(`/purchase-orders/${orderId}/pdf`, orderNumber);
  };
  
  const handleOpenEdit = (order) => {
    setEditingOrder(order);
    setEditForm({
      supplier_id: order.supplier_id || '',
      observations: order.observations || '',
      budget: order.budget ?? ''
    });
    setEditDialogOpen(true);
  };
  
  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editForm.supplier_id) { toast.error('Seleccioná un proveedor'); return; }
    try {
      const payload = {};
      if (editForm.supplier_id) payload.supplier_id = editForm.supplier_id;
      payload.observations = editForm.observations;
      payload.budget = editForm.budget === '' ? null : parseFloat(editForm.budget);
      await axios.put(`${BACKEND_URL}/api/purchase-orders/${editingOrder.order_id}`, payload, { withCredentials: true });
      toast.success('Orden de Compra actualizada');
      setEditDialogOpen(false);
      setEditingOrder(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar OC');
    }
  };
  
  const handleCancelOrder = async (order) => {
    const reason = prompt('Motivo de cancelación de la OC ' + order.order_number + ':');
    if (reason === null) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/purchase-orders/${order.order_id}?reason=${encodeURIComponent(reason)}`, { withCredentials: true });
      toast.success('OC cancelada');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cancelar OC');
    }
  };
  
  const buildShareText = (order, publicUrl) => {
    const itemsTxt = (order.items || []).map(i => `• ${i.article_name} x ${i.quantity}`).join('\n');
    return [
      `Hola${order.supplier ? ' ' + order.supplier : ''},`,
      `Adjuntamos la Orden de Compra ${order.order_number}:`,
      '',
      itemsTxt,
      '',
      order.observations ? `Observaciones: ${order.observations}` : '',
      order.budget ? `Presupuesto: $${parseFloat(order.budget).toFixed(2)}` : '',
      '',
      publicUrl ? `Descargar el comprobante PDF (válido 30 días):` : '',
      publicUrl || '',
      '',
      'Saludos.'
    ].filter(Boolean).join('\n');
  };
  
  const triggerDownload = async (order) => {
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/purchase-orders/${order.order_id}/pdf`,
        { withCredentials: true, responseType: 'blob' }
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `OC_${order.order_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (err) {
      // si falla la descarga, seguimos con el link público igual
    }
  };
  
  const getPublicLink = async (order) => {
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/purchase-orders/${order.order_id}/share-link`,
        {},
        { withCredentials: true }
      );
      return res.data?.url || '';
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo generar el link público');
      return '';
    }
  };
  
  const shareByEmail = async (order) => {
    const publicUrl = await getPublicLink(order);
    await triggerDownload(order);
    if (publicUrl) {
      toast.success('PDF descargado. El link público queda válido por 30 días.');
    }
    const subject = encodeURIComponent(`Orden de Compra ${order.order_number}`);
    const body = encodeURIComponent(buildShareText(order, publicUrl));
    window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
  };
  
  const shareByWhatsApp = async (order) => {
    const publicUrl = await getPublicLink(order);
    const text = encodeURIComponent(buildShareText(order, publicUrl));
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };
  
  const getStatusBadge = (status) => {
    const styles = {
      generated: { label: 'Generada', className: 'bg-blue-100 text-blue-800' },
      received: { label: 'Recibida', className: 'bg-green-100 text-green-800' },
      received_with_shortage: { label: 'Recibida c/Faltante', className: 'bg-orange-100 text-orange-800' },
      partial_received: { label: 'En Revisión', className: 'bg-amber-100 text-amber-800' },
      rejected: { label: 'Rechazada', className: 'bg-red-100 text-red-800' },
      cancelled: { label: 'Cancelada', className: 'bg-slate-200 text-slate-700' }
    };
    const style = styles[status] || styles.generated;
    return <Badge className={style.className}>{style.label}</Badge>;
  };
  
  // Filtrar orders según los filtros
  const filteredOrders = orders.filter(order => {
    return (
      (filters.order_number === '' || order.order_number?.toLowerCase().includes(filters.order_number.toLowerCase())) &&
      (filters.request_number === '' || order.request_number?.toLowerCase().includes(filters.request_number.toLowerCase())) &&
      (filters.supplier === '' || order.supplier?.toLowerCase().includes(filters.supplier.toLowerCase())) &&
      (filters.status === '' || order.status === filters.status)
    );
  });
  
  if (loading) {
    return <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div></div>;
  }
  
  return (
    <div className="space-y-6" data-testid="purchase-orders-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight mb-2">Órdenes de Compra</h1>
          <p className="text-muted-foreground">Gestiona las órdenes de compra autorizadas</p>
          
          {/* Indicador de filtro activo */}
          {statusFromUrl && (
            <div className="flex items-center gap-2 mt-2">
              <Badge className="bg-violet-500 text-white flex items-center gap-1">
                <FileCheck className="w-3 h-3" />
                Mostrando solo: {statusFromUrl === 'generated' ? 'Pendientes de Recepción' : statusFromUrl === 'received' ? 'Recibidas' : statusFromUrl}
                <button onClick={clearStatusFilter} className="ml-1 hover:bg-violet-600 rounded-full p-0.5">
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            </div>
          )}
        </div>
        
        {canCreate && pendingRequests.length > 0 && (
          <Button onClick={() => setCreateDialogOpen(true)} data-testid="create-order-button">
            <Plus className="w-4 h-4 mr-2" />
            Generar Orden de Compra
          </Button>
        )}
      </div>
      
      {/* Alerta de NP pendientes para usuarios con permiso de crear OC */}
      {canCreate && pendingRequests.length > 0 && (
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-amber-800">
                  {pendingRequests.length} Nota(s) de Pedido aprobadas esperando OC
                </p>
                <p className="text-sm text-amber-600">
                  Haz clic en "Generar Orden de Compra" para asignar proveedor
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  <Input
                    placeholder="Filtrar OC..."
                    value={filters.order_number}
                    onChange={(e) => setFilters({ ...filters, order_number: e.target.value })}
                    className="h-8 text-xs"
                  />
                </TableHead>
                <TableHead>Fecha</TableHead>
                <TableHead>
                  <Input
                    placeholder="Filtrar NP..."
                    value={filters.request_number}
                    onChange={(e) => setFilters({ ...filters, request_number: e.target.value })}
                    className="h-8 text-xs"
                  />
                </TableHead>
                <TableHead>
                  <Input
                    placeholder="Filtrar proveedor..."
                    value={filters.supplier}
                    onChange={(e) => setFilters({ ...filters, supplier: e.target.value })}
                    className="h-8 text-xs"
                  />
                </TableHead>
                <TableHead>Depósito</TableHead>
                <TableHead>
                  <select
                    value={filters.status}
                    onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                    className="h-8 w-full text-xs rounded-md border border-input bg-background px-2"
                  >
                    <option value="">Todos</option>
                    <option value="generated">Generada</option>
                    <option value="received">Recibida</option>
                    <option value="received_with_shortage">Recibida c/Faltante</option>
                    <option value="partial_received">En Revisión</option>
                    <option value="rejected">Rechazada</option>
                  </select>
                </TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredOrders.map((order) => (
                <TableRow key={order.order_id}>
                  <TableCell className="font-mono font-medium">{order.order_number}</TableCell>
                  <TableCell>{fmtDate(order.created_at)}</TableCell>
                  <TableCell className="font-mono text-sm">{order.request_number}</TableCell>
                  <TableCell>{order.supplier}</TableCell>
                  <TableCell>{order.warehouse_name}</TableCell>
                  <TableCell>{getStatusBadge(order.status)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={() => { setSelectedOrder(order); setDetailDialogOpen(true); }}
                        title="Ver detalle"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDownloadPDF(order.order_id, order.order_number)} title="Descargar PDF">
                        <FileText className="w-4 h-4" />
                      </Button>
                      {order.status === 'generated' && (
                        <>
                          <Button variant="ghost" size="icon" onClick={() => shareByEmail(order)} title="Compartir por email" data-testid={`share-email-${order.order_id}`}>
                            <Mail className="w-4 h-4 text-blue-600" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => shareByWhatsApp(order)} title="Compartir por WhatsApp" data-testid={`share-wa-${order.order_id}`}>
                            <MessageCircle className="w-4 h-4 text-green-600" />
                          </Button>
                          {canEdit && (
                            <Button variant="ghost" size="icon" onClick={() => handleOpenEdit(order)} title="Editar OC" data-testid={`edit-oc-${order.order_id}`}>
                              <Pencil className="w-4 h-4 text-amber-600" />
                            </Button>
                          )}
                          {canEdit && (
                            <Button variant="ghost" size="icon" onClick={() => handleCancelOrder(order)} title="Cancelar OC" data-testid={`cancel-oc-${order.order_id}`}>
                              <Ban className="w-4 h-4 text-red-600" />
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          
          {filteredOrders.length === 0 && (
            <div className="py-12 text-center text-muted-foreground">
              <FileCheck className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No hay órdenes de compra que coincidan con los filtros.</p>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Dialog para crear OC */}
      <Dialog open={createDialogOpen} onOpenChange={(open) => { setCreateDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Generar Orden de Compra</DialogTitle>
            <DialogDescription>Selecciona una Nota de Pedido aprobada y asigna el proveedor</DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Nota de Pedido Aprobada *</Label>
              <select
                value={formData.request_id}
                onChange={(e) => handleRequestSelect(e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                required
              >
                <option value="">Selecciona una NP</option>
                {pendingRequests.map(r => (
                  <option key={r.request_id} value={r.request_id}>
                    {r.request_number} - {r.warehouse_name} ({r.items?.length} items)
                  </option>
                ))}
              </select>
            </div>
            
            {selectedRequest && (
              <div className="bg-slate-50 p-4 rounded-lg space-y-2">
                <p className="font-medium">Detalle de la NP: {selectedRequest.request_number}</p>
                <p className="text-sm text-muted-foreground">Solicitado por: {selectedRequest.created_by_name}</p>
                <p className="text-sm text-muted-foreground">Aprobado por: {selectedRequest.approved_by_name}</p>
              <div className="border rounded-md max-h-[300px] overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Artículo</TableHead>
                        <TableHead className="text-center">Cantidad</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedRequest.items?.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            {item.article_name}
                            {item.is_new_article && <Badge className="ml-2 bg-blue-100 text-blue-800">NUEVO</Badge>}
                            {item.is_new_article && <Badge variant="outline" className="ml-1 text-[10px]">{item.is_medicamento ? '💊 Medicamento' : '📦 Insumo'}</Badge>}
                          </TableCell>
                          <TableCell className="text-center">
                            {item.quantity} <span className="text-xs text-muted-foreground">{item.is_new_article ? (item.unit || 'unidad') : unitOf(item.article_id)}</span>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
            
            <div>
              <Label>Proveedor *</Label>
              <SearchableSelect
                value={formData.supplier_id}
                onChange={(sid) => {
                  const sup = activeSuppliers.find(s => s.supplier_id === sid);
                  setFormData({
                    ...formData,
                    supplier_id: sid,
                    supplier: sup ? sup.business_name : ''
                  });
                }}
                options={supplierOptions}
                placeholder={activeSuppliers.length === 0 ? 'No hay proveedores activos. Cargá uno en Proveedores.' : 'Buscá por razón social o CUIT'}
                emptyLabel="Ningún proveedor coincide"
                maxResults={Infinity}
                disabled={activeSuppliers.length === 0}
                testId="supplier-select"
              />
              {activeSuppliers.length === 0 && (
                <p className="text-xs text-amber-600 mt-1">Debes cargar al menos un proveedor activo desde el módulo "Proveedores".</p>
              )}
              {/* v3.38.0 — Aviso de circuito cruzado */}
              {supplierUsage?.is_mostly_other && (
                <div className="mt-2 flex items-start gap-1.5 text-xs text-amber-800 bg-amber-50 border border-amber-300 rounded px-2 py-1" data-testid="po-other-purchase-warning">
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span>
                    Este proveedor habitualmente opera por <b>Otras Compras</b>
                    {' '}({supplierUsage.other_purchases} otras compras · {supplierUsage.receptions} recepciones).
                    Confirmá que esta OC corresponde a mercadería a inventariar.
                  </span>
                </div>
              )}
            </div>
            
            <div>
              <Label htmlFor="budget">Presupuesto asignado</Label>
              <MoneyInput
                id="budget"
                value={formData.budget}
                onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
              />
            </div>
            
            <div>
              <Label htmlFor="observations">Observaciones (condiciones comerciales/financieras)</Label>
              <Textarea
                id="observations"
                value={formData.observations}
                onChange={(e) => setFormData({ ...formData, observations: e.target.value })}
                rows={2}
                placeholder="Ej: Pago a 30 días, descuento 10%..."
              />
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={!formData.request_id || !formData.supplier_id}>
                Generar Orden de Compra
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* Dialog de detalle */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detalle de Orden de Compra</DialogTitle>
            <DialogDescription>{selectedOrder?.order_number}</DialogDescription>
          </DialogHeader>
          
          {selectedOrder && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">NP Origen</p>
                  <p className="font-medium">{selectedOrder.request_number}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Proveedor</p>
                  <p className="font-medium">{selectedOrder.supplier}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Solicitado por</p>
                  <p className="font-medium">{selectedOrder.requested_by_name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Aprobado por</p>
                  <p className="font-medium">{selectedOrder.approved_by_name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Autorizado por</p>
                  <p className="font-medium">{selectedOrder.created_by_name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Depósito Destino</p>
                  <p className="font-medium">{selectedOrder.warehouse_name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Presupuesto asignado</p>
                  <p className="font-medium">{selectedOrder.budget ? `$${parseFloat(selectedOrder.budget).toFixed(2)}` : 'No especificado'}</p>
                </div>
              </div>
              
              <div className="border rounded-md max-h-[400px] overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Artículo</TableHead>
                      <TableHead className="text-center">Cantidad</TableHead>
                      <TableHead className="text-center">Unidad</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedOrder.items?.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          {item.article_name}
                          {item.is_new_article && <Badge variant="outline" className="ml-2 text-[10px]">{item.is_medicamento ? '💊 Medicamento' : '📦 Insumo'}</Badge>}
                        </TableCell>
                        <TableCell className="text-center">{item.quantity}</TableCell>
                        <TableCell className="text-center text-muted-foreground">{item.is_new_article ? (item.unit || 'unidad') : unitOf(item.article_id)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              
              {selectedOrder.observations && (
                <div>
                  <p className="text-sm text-muted-foreground">Observaciones (condiciones comerciales/financieras)</p>
                  <p className="bg-slate-50 p-3 rounded">{selectedOrder.observations}</p>
                </div>
              )}
              
              <div className="border-t pt-4">
                <Attachments
                  entityType="purchase_order"
                  entityId={selectedOrder.order_id}
                  currentUser={user}
                  canUpload={selectedOrder.status === 'generated' && canEdit}
                />
              </div>
              
              {selectedOrder.status === 'generated' && (
                <div className="flex gap-2 pt-2 border-t">
                  <Button variant="outline" size="sm" onClick={() => shareByEmail(selectedOrder)}>
                    <Mail className="w-4 h-4 mr-2" /> Compartir por Email
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => shareByWhatsApp(selectedOrder)}>
                    <MessageCircle className="w-4 h-4 mr-2" /> Compartir por WhatsApp
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Dialog de edición */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Editar Orden de Compra</DialogTitle>
            <DialogDescription>{editingOrder?.order_number}</DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSaveEdit} className="space-y-4">
            <div>
              <Label>Proveedor *</Label>
              <SearchableSelect
                value={editForm.supplier_id}
                onChange={(sid) => setEditForm({ ...editForm, supplier_id: sid })}
                options={supplierOptions}
                placeholder="Buscá por razón social o CUIT"
                emptyLabel="Ningún proveedor coincide"
                maxResults={Infinity}
                testId="edit-supplier-select"
              />
            </div>
            <div>
              <Label>Presupuesto asignado</Label>
              <MoneyInput
                value={editForm.budget}
                onChange={(e) => setEditForm({ ...editForm, budget: e.target.value })}
              />
            </div>
            <div>
              <Label>Observaciones</Label>
              <Textarea
                value={editForm.observations}
                onChange={(e) => setEditForm({ ...editForm, observations: e.target.value })}
                rows={3}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" data-testid="save-oc-edit">Guardar Cambios</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default PurchaseOrders;
